import React, { useState, useEffect, useRef } from "react";
import { signIn, getCurrentSession, signOut } from "./auth";
import config from "./config";
import "./App.css";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    getCurrentSession()
      .then((session) => setUser(session))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      const session = await signIn(email, password);
      setUser(session);
    } catch (err) {
      setAuthError(err.message || "Sign in failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = () => {
    signOut();
    setUser(null);
    setMessages([]);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || chatLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);

    try {
      // Build history from previous messages (exclude the current one)
      const history = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const res = await fetch(config.apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: user.idToken,
        },
        body: JSON.stringify({ message: userMessage, history }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Request failed");
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          metrics: data.metrics,
          model: data.model,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${err.message}`, isError: true },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  // Login screen
  if (!user) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>GenAI Chat</h1>
          <p className="subtitle">Powered by Amazon Bedrock</p>
          <form onSubmit={handleSignIn}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {authError && <div className="error">{authError}</div>}
            <button type="submit" disabled={authLoading}>
              {authLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Chat screen
  return (
    <div className="chat-container">
      <header className="chat-header">
        <h1>GenAI Chat</h1>
        <div className="header-right">
          <span className="user-email">{user.email}</span>
          <button onClick={handleSignOut} className="sign-out-btn">
            Sign Out
          </button>
        </div>
      </header>

      <div className="messages">
        {messages.length === 0 && (
          <div className="empty-state">
            <p>Send a message to start chatting with Claude Sonnet 4.6</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`message ${msg.role} ${msg.isError ? "error-msg" : ""}`}
          >
            <div className="message-role">
              {msg.role === "user" ? "You" : "Assistant"}
            </div>
            <div className="message-content">{msg.content}</div>
            {msg.metrics && (
              <div className="message-metrics">
                <span>⏱ {msg.metrics.latency_ms}ms</span>
                <span>📊 {msg.metrics.total_tokens} tokens</span>
                <span>📥 {msg.metrics.input_tokens} in</span>
                <span>📤 {msg.metrics.output_tokens} out</span>
              </div>
            )}
          </div>
        ))}
        {chatLoading && (
          <div className="message assistant">
            <div className="message-role">Assistant</div>
            <div className="message-content typing">Thinking...</div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="input-form" onSubmit={handleSend}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your message..."
          disabled={chatLoading}
          autoFocus
        />
        <button type="submit" disabled={chatLoading || !input.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}

export default App;
